/**
 * @license
 * @copyright Copyright Motili Inc., 2024 All Rights Reserved
 */

import uniq from 'lodash/uniq';

import * as ApiService from './ApiService';
import { isProductAvailable } from '../utils/Validator';

const baseUrl = 'products';

/**
 * motili product autosuggest api
 * @param {string} value
 * @param {string} tradeTypeId
 * @param {string} tradeSubTypeId
 * @param {object | []} [additionalFilter]
 * @returns {Promise<any>} response
 */
export function suggest(value, tradeTypeId, tradeSubTypeId, additionalFilter) {
    return ApiService.get(true, 'Products/suggestions', {
        prefix: value,
        minPrefexLength: undefined,
        limit: 10,
        tradeTypeId,
        tradeSubTypeId,
        additionalFilter,
    });
}

export function suggestDisplay(value, id, tradeTypeId, tradeSubTypeId) {
    const query = {
        filter: {
            where: {
                and: [
                    { tradeSubTypeId },
                    { tradeTypeId },
                    { display: { like: `${value.toUpperCase()}%` } },
                    { id: { neq: id } },
                ],
                active: true,
                system: false,
            },
            fields: [
                'id',
                'sku',
                'tradeTypeId',
                'tradeSubTypeId',
                'display',
                'details',
            ],
            limit: 10,
        },
    };

    return find(query);
}

export const ProductAdjustmentTypes = {
    MARGIN: {
        id: 'MARGIN',
        display: 'Margin',
        type: 'number',
        mask: 'currency',
        min: 0,
        max: 99,
    },
    MARKUP: {
        id: 'MARKUP',
        display: 'Markup',
        type: 'number',
        mask: 'currency',
        min: 0,
        max: 999,
    },
    FIXED: {
        id: 'FIXED',
        display: 'Fixed',
        type: 'number',
        mask: 'currency',
        min: 0,
        max: 999999999,
    },
};

/**
 * calculate base on type (margin, markup, fixed)
 * @param {string} adjustmentTypeId
 * @param {number} baseValue
 * @param {number} adjustValue
 * @returns {number}
 * @private
 */
export function calculate(adjustmentTypeId, baseValue, adjustValue) {
    if (!adjustmentTypeId) return baseValue;
    switch (adjustmentTypeId) {
        case 'MARGIN': {
            return _calculateMargin(baseValue, adjustValue);
        }
        case 'MARKUP': {
            return _calculateMarkup(baseValue, adjustValue);
        }
        case 'FIXED': {
            return adjustValue;
        }
        default: {
            throw new Error('Unknown product adjustment type id');
        }
    }
}

export const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
});

/**
 * _calculateMargin
 * @param {number} x
 * @param {number} margin
 * @return {number}
 */
function _calculateMargin(x, margin) {
    return x / (1 - margin);
}

/**
 * _calculateMarkup
 * @param {number} x
 * @param {number} markup
 * @return {number}
 */
function _calculateMarkup(x, markup) {
    return x * (1 + markup);
}

/**
 * Calculate margin value in percentage
 * @param {number} cost
 * @param {number} price
 * @return {number} margin percentage
 */
export function calculateMarginPercentage(cost, price) {
    return (100 * (price - cost)) / price;
}

/**
 * Calculate markup value in percentage
 * @param {number} cost
 * @param {number} price
 * @return {number} markup percentage
 */
export function calculateMarkupPercentage(cost, price) {
    return (100 * (price - cost)) / cost;
}

export async function findBySKU(sku, scope) {
    return find({
        filter: {
            where: { sku },
            fields: [
                'id',
                'sku',
                'tradeTypeId',
                'tradeSubTypeId',
                'productCategoryId',
                'productSubCategoryId',
                'display',
                'details',
                'img',
                'imgPrimary',
                'hasSuppliedDescription',
                'hasSuppliedPayout',
                'hasSuppliedVendorCost',
            ],
            include: [
                {
                    relation: 'price',
                    fields: [
                        'price',
                        'priceTaxPercent',
                        'priceTax',
                        'priceIncludingTax',
                    ],
                },
                {
                    relation: 'cost',
                    fields: ['basePayout', 'cost'],
                },
                {
                    relation: 'attributes',
                    fields: [
                        'id',
                        'attributeTypeId',
                        'attributeSubTypeId',
                        'isFeature',
                        'featureDisplay',
                        'value',
                    ],
                },
                {
                    relation: 'laborProduct',
                },
                {
                    relation: 'packagedProducts',
                },
            ],
        },
        scope: { ...scope },
    });
}

// TODO: THIS BLOCK REPRESENTS VETTED METHODS

/**
 *
 * @param {*} id
 * @param {*} query
 */
export function findById(id, query) {
    return ApiService.get(true, `${baseUrl}/${id}`, query);
}

/**
 * @param {Object} query
 * @todo use search mixin paradigm, need to solve for before/after find remote hooks on product api
 */
export function find(query, scope) {
    return ApiService.get(true, baseUrl, { ...query, ...scope });
}

/**
 * @todo use search mixin paradigm, need to solve for before/after find remote hooks on product api
 * @param {*} query
 */
export function count(query) {
    return ApiService.get(true, `${baseUrl}/count`, query);
}

// DO NOT USE THESE THEY ARE DEPRECATED AND ONLY INCLUDED HERE TO SUPPORT THE LEGACY "INVENTORY" VIEW

export function fetchProductCategories() {
    return ApiService.get(true, `${baseUrl}/categories`);
}

/**
 * @param {Object} query
 */
export function getProductCategories(query, scope) {
    const _query = { ...query, ...scope };
    return ApiService.get(true, `${baseUrl}/categories`, _query);
}

function initLineItems(req) {
    return ApiService.post(true, `${baseUrl}/initLineItems`, req);
}

function updateLineItem(lineItem) {
    return ApiService.post(true, `${baseUrl}/updateLineItem`, lineItem);
}

/**
 * Validate all product ids are active or available to use
 * @param {number[]} productIds
 */
export async function isAllProductsActive(productIds, country = 'US') {
    const uniqueIds = uniq(productIds);
    if (!uniqueIds || uniqueIds.length === 0) return true;
    const products = await find({
        filter: {
            where: { id: { inq: uniqueIds } },
            include: [
                {
                    relation: 'bundleProducts',
                    scope: {
                        include: [
                            {
                                relation: 'product',
                                scope: {
                                    fields: [
                                        'id',
                                        'active',
                                        'sku',
                                        'availableInCountries',
                                    ],
                                },
                            },
                        ],
                    },
                },
            ],
            fields: ['id', 'active', 'sku', 'availableInCountries'],
        },
    });
    if (!products || uniqueIds.length !== products.length) {
        return false;
    }
    return !products.some(p => !isProductAvailable(p, country));
}

export function findProductAddOn(query, scope) {
    return ApiService.get(true, `${baseUrl}/listProductAddOn`, {
        ...query,
        ...scope,
    });
}

export function getProductAddOns(activeFilter, where, scope) {
    if (!activeFilter) {
        return [];
    }
    const query = {
        filter: {
            where: {
                and: [
                    { productTradeTypeId: activeFilter.tradeTypeId },
                    { productTradeSubTypeId: activeFilter.tradeSubTypeId },
                    ...where,
                ],
            },
            fields: ['id', 'productId'],
            include: [
                {
                    relation: 'product',
                    scope: {
                        fields: [
                            'id',
                            'sku',
                            'tradeTypeId',
                            'tradeSubTypeId',
                            'productCategoryId',
                            'productSubCategoryId',
                            'display',
                            'details',
                            'img',
                            'imgPrimary',
                            'availableInCountries',
                        ],
                        include: [
                            {
                                relation: 'attributes',
                                scope: {
                                    fields: [
                                        'attributeTypeId',
                                        'attributeSubTypeId',
                                        'value',
                                    ],
                                },
                            },
                        ],
                    },
                },
            ],
        },
    };
    return findProductAddOn({ ...query, ...scope });
}

export function getProductById(id, scope) {
    return _getProduct(id, undefined, scope);
}

export function _getProduct(id, sku, scope) {
    const query = {
        filter: {
            where: {},
        },
    };
    if (id) {
        query.filter.where.id = id;
    }
    if (sku) {
        query.filter.where.sku = sku;
    }
    const _query = { ...query, scope };
    return find(_query).then(products => {
        if (!Array.isArray(products) || products.length === 0) {
            return Promise.reject(
                new Error(
                    'Product unavailable or restricted. Contact Motili for more details'
                )
            );
        }
        if (products.length > 1) {
            return Promise.reject(
                new Error('More than one product was returned')
            );
        }
        return products[0];
    });
}

// END

export default {
    ProductAdjustmentTypes,
    calculate,
    currencyFormatter,
    findBySKU,
    find,
    fetchProductCategories,
    initLineItems,
    updateLineItem,
    getProductById,
    _getProduct,
};
